import { BasicStore } from '@bridebook/web/app-basic/lib/types';
import { isEmpty } from 'ramda';
import { createSelector } from 'reselect';
import { PremiumTiers } from '@bridebook/toolbox/src/constants';
import { isSeoScoreIndexable } from '@bridebook/toolbox/src/elastic-sync/utils/get-seo-score';
import gazetteer, { CountryCodes, Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import {
  getPhotoUrl,
  getPhotosWithPath,
  getPricingCategoryByIndicator,
} from '@bridebook/web/app-shared/lib/supplier/utils';

const _getSupplierDetails = (state: BasicStore) => state.supplierDetails;

export const getSupplier = (state: BasicStore) => state.supplier.supplier;
const getTypeVenue = createSelector(getSupplier, (supplier) => supplier?.typeDetails.venue);
const getSupplierCounters = createSelector(getSupplier, (supplier) => supplier?.counters);
export const getSupplierLoaded = (state: BasicStore) => state.supplier.loaded;

export const getSupplierId = createSelector(getSupplier, (supplier) => supplier?.id);
export const getSupplierType = createSelector(getSupplier, (supplier) => supplier?.type?.[0]);
export const getIsPhotographer = createSelector(getSupplierType, (type) => type === 'photo');
export const getIsVenue = createSelector(getSupplierType, (type) => type === 'venue');
export const getSupplierPhotos = createSelector(_getSupplierDetails, ({ photos }) => photos);

export const getTypeVenueDetails = createSelector(getTypeVenue, (venue) => venue?.details || []);
export const getTypeVenueCeremonyReception = createSelector(
  getSupplier,
  (supplier) => supplier?.typeDetails.venue?.ceremonyReception || [],
);
export const getSupplierAchievements = createSelector(
  _getSupplierDetails,
  ({ achievements }) => achievements,
);
export const getSupplierFeedbackCount = createSelector(
  getSupplierCounters,
  (counters) => (counters?.reviews || 0) + (counters?.testimonials || 0),
);
export const getSupplierAccordions = createSelector(
  _getSupplierDetails,
  ({ accordions }) => accordions,
);
export const getVenuePackages = createSelector(
  [_getSupplierDetails],
  (supplier) => supplier.packages,
);

export const getSupplierName = createSelector([getSupplier], (supplier) => supplier?.name);

export const getSupplierPackages = createSelector(
  [_getSupplierDetails],
  (supplier) => supplier.supplierPackages,
);

export const getSupplierReviews = createSelector([_getSupplierDetails], ({ reviews }) => reviews);

export const getSupplierPricingCategory = createSelector(getSupplier, (supplier) =>
  getPricingCategoryByIndicator(supplier?.pricing?.indicator),
);

export const getSupplierCurrencyCode = createSelector(
  getSupplier,
  (supplier) => supplier?.l10n?.currency || 'GBP',
);

export const getSupplierCurrencySymbol = createSelector(getSupplierCurrencyCode, (currencyCode) =>
  Gazetteer.getCurrencySymbol(currencyCode),
);

export const getSupplierCountry = createSelector(
  getSupplier,
  (supplier) => supplier?.l10n?.country || CountryCodes.GB,
);

export const getSupplierAdminAreas = createSelector(
  getSupplier,
  (supplier) => supplier?.address?.adminArea,
);

export const getSupplierCounty = createSelector(
  [getSupplierCountry, getSupplierAdminAreas],
  (countryCode, adminAreas) =>
    gazetteer.getMarketByCountry(countryCode).sortAdminAreas(adminAreas || [], 1)[0],
);

export const getVenueSupplierDiningCapacity = createSelector(getTypeVenue, (venue) => {
  // According to the state interface, diningCapacity should be a number, but it might also
  // occur as a string.
  type CastedDiningCapacity = number | string | undefined;

  const diningCapacity = venue?.capacity?.dining as CastedDiningCapacity;
  const normalizedDiningCapacity = diningCapacity || 0;
  return typeof normalizedDiningCapacity === 'string'
    ? parseInt(normalizedDiningCapacity, 10)
    : diningCapacity;
});
export const getIsVenueSupplierAccomodationRooms = createSelector(
  getTypeVenue,
  (venue) => venue?.accommodation?.rooms || 0,
);

export const getHasInstantBrochures = createSelector(
  [_getSupplierDetails, getIsVenue],
  (supplier, isVenue) => isVenue && !isEmpty(supplier.brochures),
);

export const getSupplierPhotosCount = createSelector(
  [getSupplier],
  (supplier) => supplier?.counters?.photos || 0,
);

export const getPremium = createSelector([_getSupplierDetails], (supplier) => supplier?.premium);

export const getSupplierVideosCount = createSelector(
  [getSupplier],
  (supplier) => supplier?.counters?.videos || 0,
);

export const getVideoToursCount = createSelector([getSupplier, getPremium], (supplier, premium) => {
  const supplierCountry = supplier?.l10n?.country;
  const supplierType = supplier?.type?.[0];

  if (!supplierCountry || !supplierType) {
    return 0;
  }

  const market = gazetteer.getMarketByCountry(supplierCountry);
  const minTierRequired = market.getSettingRequiredTier('videoTours', supplierType);

  if ((premium?.tier || PremiumTiers.Tier_0) >= minTierRequired) {
    return supplier?.counters?.videoTours || 0;
  }
  return 0;
});

export const isSupplierRegistered = createSelector(
  [getSupplier],
  (supplier) => Number(supplier?.users?.length) > 0,
);

export const selectRecommendedPhotographer = createSelector([_getSupplierDetails], (supplier) =>
  supplier.relations?.find((relation) => relation.type === 'photo'),
);

export const getSpecialOffers = createSelector(
  [_getSupplierDetails],
  (supplier) => supplier.offers,
);

export const getQuestions = createSelector([_getSupplierDetails], (supplier) => supplier.questions);

export const getFairs = createSelector([_getSupplierDetails], (supplier) => supplier.fairs);

export const getFeedbackCount = createSelector(
  [getSupplier],
  (supplier) => Number(supplier?.counters?.reviews) + Number(supplier?.counters?.testimonials) || 0,
);

export const getIsSupplierDeleted = createSelector(
  [_getSupplierDetails],
  (supplier) => supplier?.deleted,
);

export const getSupplierErrorCode = createSelector(
  [_getSupplierDetails],
  (supplier) => supplier.errorCode,
);

// https://bridebook.atlassian.net/browse/LIVE-21567 - use SEO score only as a criteria
export const getSupplierSeoIndex = createSelector([_getSupplierDetails], (supplier) =>
  isSeoScoreIndexable(supplier.seoScore),
);

export const getBasePrice = createSelector(getSupplier, (supplier) => supplier?.pricing?.base);

export const getWeddingsServedRange = createSelector(
  getSupplier,
  (supplier) => supplier?.company?.weddingsHosted,
);

export const getAllSupplierPhotos = createSelector(_getSupplierDetails, (supplier) =>
  getPhotosWithPath(supplier.photos).map((photo) => ({
    ...photo,
    path: getPhotoUrl(photo),
  })),
);

export const getSupplierAvatar = createSelector(getAllSupplierPhotos, (photos) => photos?.[0]);

export const getSupplierVideos = createSelector([_getSupplierDetails], (supplier) =>
  supplier.videos.filter(({ type }) => type === 'video'),
);

export const getSupplierVideoTours = createSelector([_getSupplierDetails], (supplier) =>
  supplier.videos.filter(({ type }) => type === 'videoTour'),
);

export const getSupplierBadges = createSelector(
  _getSupplierDetails,
  (supplier) => supplier.badges || [],
);

export const getInstagramMedia = createSelector(
  _getSupplierDetails,
  (supplier) => supplier.instagramMedia,
);

export const getRecommendations = createSelector(
  _getSupplierDetails,
  (supplier) => supplier.recommendations,
);

export const getInstagramUrl = createSelector(
  getSupplier,
  (supplier) => supplier?.social?.instagram,
);

export const getSupplierTier = createSelector(getPremium, (premium) => premium?.tier);
